import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    fetchCustomProfileSessions,
    createCustomProfileSession,
    deleteCustomProfileSession,
    duplicateCustomProfileSession,
} from "../api";

export type CustomProfileOperationType =
    | "Generation_NCD"
    | "Generation_CD"
    | "Validation";

export interface CustomProfileSession {
    id: string;
    name: string;
    operations: CustomProfileOperationType[];
    createdAt: string;
    updatedAt: string;
}

export interface CustomProfilesState {
    isLoading: boolean;
    sessions: CustomProfileSession[];
}

const initialState: CustomProfilesState = {
    isLoading: false,
    sessions: [],
};

const OPERATIONS_STORAGE_KEY = "customProfileOperations";

const loadOperationsFromStorage = (): Record<string, CustomProfileOperationType[]> => {
    try {
        const stored = localStorage.getItem(OPERATIONS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const saveOperationsToStorage = (sessionId: string, operations: CustomProfileOperationType[]) => {
    try {
        const stored = loadOperationsFromStorage();
        stored[sessionId] = operations;
        localStorage.setItem(OPERATIONS_STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // ignore storage errors
    }
};

const removeOperationsFromStorage = (sessionId: string) => {
    try {
        const stored = loadOperationsFromStorage();
        delete stored[sessionId];
        localStorage.setItem(OPERATIONS_STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // ignore storage errors
    }
};

export const customProfilesSlice = createSlice({
    name: "customProfiles",
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSessions: (state, action: PayloadAction<CustomProfileSession[]>) => {
            state.sessions = action.payload;
        },
        addSession: (state, action: PayloadAction<CustomProfileSession>) => {
            state.sessions.unshift(action.payload);
        },
        removeSession: (state, action: PayloadAction<string>) => {
            state.sessions = state.sessions.filter((s) => s.id !== action.payload);
        },
        updateSession: (state, action: PayloadAction<CustomProfileSession>) => {
            const idx = state.sessions.findIndex((s) => s.id === action.payload.id);
            if (idx !== -1) state.sessions[idx] = action.payload;
        },
    },
});

const { setIsLoading, setSessions, addSession, removeSession, updateSession } =
    customProfilesSlice.actions;

export { updateSession };

export const getCustomProfileSessions =
    () => async (dispatch: any) => {
        dispatch(setIsLoading(true));
        try {
            const sessions = await fetchCustomProfileSessions();
            if (!sessions) return; // the request failed and restClient already told the user why
            const storedOps = loadOperationsFromStorage();

            const sessionsWithOps = sessions.map((s: any) => {
                const backendOps = s.operations ?? [];
                const localOps = storedOps[s.id] ?? [];
                const mergedOps = backendOps.length > 0 ? backendOps : localOps;
                return { ...s, operations: mergedOps };
            });

            dispatch(setSessions(sessionsWithOps));
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const createNewCustomProfileSession =
    (name: string, operations: CustomProfileOperationType[]) =>
        async (dispatch: any): Promise<CustomProfileSession | null> => {
            try {
                const session = await createCustomProfileSession(name);
                const enriched: CustomProfileSession = { ...session, operations };
                saveOperationsToStorage(session.id, operations);
                dispatch(addSession(enriched));
                return enriched;
            } catch {
                // restClient already showed the server's reason
                return null;
            }
        };

export const updateSessionOperations =
    (sessionId: string, operations: CustomProfileOperationType[]) =>
        (dispatch: any, getState: any) => {
            const state = getState();
            const session = state.customProfiles.sessions.find((s: CustomProfileSession) => s.id === sessionId);
            if (session) {
                const updated = { ...session, operations };
                saveOperationsToStorage(sessionId, operations);
                dispatch(updateSession(updated));
            }
        };

export const deleteCustomProfile =
    (id: string) => async (dispatch: any) => {
        try {
            await deleteCustomProfileSession(id);
            removeOperationsFromStorage(id);
            dispatch(removeSession(id));
        } catch {
            // restClient already showed the server's reason
        }
    };
